import type { Meta } from "./api";
import { fecha, numero } from "./formato";

/**
 * La frase de B13 para cada meta: «¿vamos a llegar y dónde falta?», no
 * «¿cuántos llevamos?». El veredicto lo calcula el servidor (`tablero.proyectar`,
 * con el día de hoy); aquí se le ponen las palabras, y se prueban, porque son
 * las palabras con las que alguien decide si consigue más gente o baja la
 * meta.
 *
 * Honesta quiere decir: sin ritmo no se dice fecha; «cumplida» solo con el
 * número; y «en riesgo» siempre dice cuánto haría falta.
 */
export interface FraseDeMeta {
  titulo: string;
  detalle: string;
  tono: "exito" | "aviso" | "peligro" | "neutro" | "primario";
}

export function fraseDeMeta(m: Meta): FraseDeMeta {
  const p = m.proyeccion;
  const ritmo = `${p.ritmo_diario} al día`;
  switch (p.estado) {
    case "cumplida":
      return {
        titulo: "Cumplida",
        detalle: `${numero(m.logrado)} de ${numero(m.objetivo)}.`,
        tono: "exito",
      };
    case "vencida":
      return {
        titulo: `Venció el ${fecha(m.periodo_fin)}`,
        detalle: `Quedó en ${numero(m.logrado)} de ${numero(m.objetivo)}: ${numero(p.faltante)} de menos. Ciérrala o muévele la fecha.`,
        tono: "peligro",
      };
    case "sin_ritmo":
      return {
        titulo: "Sin capturas en 14 días",
        detalle:
          `Faltan ${numero(p.faltante)}.` +
          (p.ritmo_necesario != null && m.periodo_fin
            ? ` Para llegar el ${fecha(m.periodo_fin)} hacen falta ${numero(p.ritmo_necesario)} al día.`
            : ""),
        tono: "peligro",
      };
    case "en_riesgo":
      return {
        titulo: "A este ritmo no llega",
        detalle:
          `Faltan ${numero(p.faltante)}; hoy entran ${ritmo} y así llegaría el ${fecha(p.fecha_estimada)}, ` +
          `después del ${fecha(m.periodo_fin)}. Hacen falta ${numero(p.ritmo_necesario)} al día.`,
        tono: "aviso",
      };
    case "a_tiempo":
      return {
        titulo: "Va a tiempo",
        detalle: `Faltan ${numero(p.faltante)}; a ${ritmo} llega el ${fecha(p.fecha_estimada)}, antes del ${fecha(m.periodo_fin)}.`,
        tono: "exito",
      };
    case "en_marcha":
    default:
      return {
        titulo: "En marcha, sin fecha límite",
        detalle: `Faltan ${numero(p.faltante)}; a ${ritmo} llega el ${fecha(p.fecha_estimada)}.`,
        tono: "primario",
      };
  }
}

export function nombreDeMeta(m: Meta): string {
  return m.nombre?.trim() ? m.nombre : `${m.tipo} ${m.clave}`;
}
